/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaCheck, FaArrowRight, FaPlus, FaMinus
} from 'react-icons/fa';

// Définition des couleurs modernes
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  success: '#06d6a0',
  warning: '#ffbe0b',
  dark: '#1e2a3a',
  darkBlue: '#0f172a',
  light: '#fcfcfc',
  glass: 'rgba(255, 255, 255, 0.1)',
  glassDark: 'rgba(14, 25, 45, 0.9)',
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Pricing plans data
const pricingPlans = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Idéal pour les petites entreprises de transport sanitaire',
    price: 'Sur devis',
    priceDescription: 'par véhicule/mois (HT)',
    color: colors.primary,
    features: [
      'Régulation',
      'Facturation',
      'Plannings',
      'Equipages',
      'Pointeuse',
      'Parc automobile',
      'Gestion du personnel',
      'Gestion des établissements',
      'Support client',
    ],
  },
  {
    id: 'advanced',
    name: 'Avancée',
    description: 'Pour les entreprises en croissance avec des besoins étendus',
    price: 'Sur devis',
    priceDescription: 'par véhicule/mois (HT)',
    color: colors.accent,
    popular: true,
    features: [
      'Offre Standard +',
      'Géolocalisation',
      'Application mobile',
      'Gestion des stocks',
      'Gestion du matériel médical',
      'Documents',
      'Gestion multi-société',
      'Fournisseurs',
      'Locaux',
      'Agenda',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Solution complète pour les entreprises exigeantes',
    price: 'Sur devis',
    priceDescription: 'par véhicule/mois (HT)',
    color: colors.secondary,
    features: [
      'Offre Avancée +',
      'Tableau de bord',
      'Données d\'exploitation',
      'Processus',
      'Gestion des tâches',
    ],
  }
];

// Optimisation: créer un composant de carte memoizé
const PricingCard = memo(({ plan, isSelected, onSelect }) => (
  <motion.div
    key={plan.id}
    variants={itemVariant}
    style={{
      flex: '1 1 300px',
      maxWidth: '380px',
      minWidth: '300px',
      position: 'relative',
      backgroundColor: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: isSelected
        ? `0 15px 30px rgba(0, 0, 0, 0.1), 0 0 0 2px ${plan.color}`
        : '0 10px 30px rgba(0, 0, 0, 0.05)',
      transition: 'box-shadow 0.3s ease',
    }}
    onClick={onSelect}
  >
    {plan.popular && (
    <div style={{
      position: 'absolute',
      top: '15px',
      right: '15px',
      background: 'linear-gradient(45deg, #06d6a0, #06d6a0cc)',
      color: 'white',
      fontSize: '0.85rem',
      fontWeight: '600',
      padding: '5px 15px',
      borderRadius: '30px',
      boxShadow: '0 4px 15px rgba(6, 214, 160, 0.3)',
      zIndex: 5,
    }}
    >
      Populaire
    </div>
    )}

    <div style={{
      height: '10px',
      backgroundColor: plan.color,
      width: '100%',
    }}
    />

    <div style={{ padding: '40px 30px' }}>
      <h3 style={{
        fontSize: '1.8rem',
        fontWeight: '700',
        marginBottom: '10px',
        color: colors.dark,
      }}
      >
        {plan.name}
      </h3>

      <p style={{
        fontSize: '1rem',
        color: '#64748b',
        marginBottom: '30px',
        minHeight: '50px',
      }}
      >
        {plan.description}
      </p>

      <div style={{
        fontSize: '2.5rem',
        fontWeight: '800',
        color: colors.dark,
        marginBottom: '5px',
      }}
      >
        {plan.price}
      </div>

      <p style={{
        fontSize: '0.95rem',
        color: '#64748b',
        marginBottom: '30px',
      }}
      >
        {plan.priceDescription}
      </p>

      <Link
        to="/contact#form"
        style={{
          backgroundColor: isSelected ? plan.color : 'transparent',
          color: isSelected ? 'white' : plan.color,
          border: `2px solid ${plan.color}`,
          borderRadius: '12px',
          padding: '15px 30px',
          fontSize: '1rem',
          fontWeight: '600',
          textDecoration: 'none',
          marginBottom: '30px',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
        }}
      >
        <span>Demander un devis</span>
        <FaArrowRight />
      </Link>

      <div style={{
        borderTop: '1px solid rgba(0, 0, 0, 0.05)',
        paddingTop: '20px',
      }}
      >
        <h4 style={{
          fontSize: '1.1rem',
          fontWeight: '600',
          marginBottom: '20px',
          color: colors.dark,
          textAlign: 'left',
        }}
        >
          Fonctionnalités incluses:
        </h4>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          textAlign: 'left',
        }}
        >
          {plan.features.map((feature, index) => (
            <li
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '12px',
                fontSize: '0.95rem',
                color: colors.dark,
              }}
            >
              <FaCheck style={{ color: plan.color }} />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
));

// Optimisation: créer des composants memoizés pour les sections FAQ
const FAQItem = memo(({
  title, content, isOpen, onClick,
}) => (
  <motion.div
    layout
    style={{
      marginBottom: '15px',
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.03)',
      border: '1px solid rgba(0, 0, 0, 0.03)',
      position: 'relative',
    }}
    transition={{
      duration: 0.3, type: 'spring', stiffness: 300, damping: 30
    }}
  >
    {/* Effet de lumière décoratif */}
    <div
      style={{
        position: 'absolute',
        top: '-50px',
        left: '-50px',
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${isOpen ? 'rgba(58, 134, 255, 0.08)' : 'rgba(0, 0, 0, 0)'} 0%, transparent 70%)`,
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.5s ease',
        zIndex: 0
      }}
    />

    <motion.div
      onClick={onClick}
      style={{
        padding: '20px 25px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1,
        background: isOpen ? 'linear-gradient(135deg, rgba(58, 134, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)' : 'transparent',
        transition: 'all 0.3s ease'
      }}
      whileHover={{
        background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.08) 0%, rgba(255, 255, 255, 0) 100%)'
      }}
    >
      <h3 style={{
        fontSize: '1.2rem',
        fontWeight: '600',
        color: isOpen ? colors.primary : colors.dark,
        margin: 0,
        flex: 1,
        transition: 'color 0.3s ease'
      }}
      >
        {title}
      </h3>
      <motion.div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isOpen ? colors.primary : 'rgba(0, 0, 0, 0.05)',
          color: isOpen ? 'white' : '#64748b',
          transition: 'all 0.3s ease'
        }}
        whileHover={{
          background: isOpen ? colors.primary : 'rgba(0, 0, 0, 0.1)',
        }}
        animate={{ rotate: isOpen ? 180 : 0 }}
      >
        {isOpen ? <FaMinus size={12} /> : <FaPlus size={12} />}
      </motion.div>
    </motion.div>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <div style={{
            padding: '0 25px 25px',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
            marginTop: '5px'
          }}
          >
            <p style={{
              color: '#64748b',
              lineHeight: '1.7',
              margin: 0,
              fontSize: '1rem',
              position: 'relative',
              zIndex: 1
            }}
            >
              {content}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
));

function PricingPage() {
  const [selectedPlan, setSelectedPlan] = useState('advanced');
  const [openFaqIndex, setOpenFaqIndex] = useState(0); // Premier élément ouvert par défaut

  // Optimisation: useCallback pour les gestionnaires d'événements
  const handleSelectPlan = useCallback((planId) => {
    setSelectedPlan(planId);
  }, []);

  const toggleFaqItem = useCallback((index) => {
    setOpenFaqIndex((prev) => (prev === index ? -1 : index));
  }, []);

  // Données FAQ
  const faqItems = [
    {
      title: 'Comment est calculé le prix de Drivesoft ?',
      content: "Le prix de Drivesoft est calculé en fonction du nombre de véhicules et de la formule choisie. Nous proposons des tarifs dégressifs selon le volume pour s'adapter à toutes les tailles d'entreprises."
    },
    {
      title: "Est-ce que je peux changer de formule en cours d'abonnement ?",
      content: 'Oui, vous pouvez évoluer vers une formule supérieure à tout moment. Nous ajusterons votre facturation au prorata temporis pour une transition sans rupture.'
    },
    {
      title: 'Y a-t-il des frais de mise en service ?',
      content: "Les frais de mise en service dépendent de vos besoins spécifiques en termes d'installation, de formation et de migration de données. Ils sont inclus dans le devis personnalisé que nous établissons."
    },
    {
      title: 'Comment se déroule le support client ?',
      content: "Tous nos clients bénéficient d'un support technique disponible par téléphone et email. Les modalités précises dépendent de la formule choisie, avec des temps de réponse garantis pour les formules Avancée et Premium."
    },
    {
      title: 'Le logiciel est-il compatible avec mon matériel existant ?',
      content: "Drivesoft est optimisé pour fonctionner sur tous les systèmes modernes. Notre équipe technique vous accompagne pour assurer la compatibilité avec votre infrastructure existante et optimiser l'intégration."
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      padding: '140px 20px 100px',
      backgroundColor: '#f8f9fa',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    }}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <h1 style={{
          fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
          fontWeight: '800',
          color: colors.dark,
          marginBottom: '20px',
          position: 'relative',
        }}
        >
          <span>Nos </span>
          <span style={{
            background: 'linear-gradient(90deg, #3a86ff, #8338ec)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
          >tarifs
          </span>
        </h1>

        <p style={{
          fontSize: 'clamp(1.1rem, 2vw, 1.3rem)',
          color: '#64748b',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: '1.7',
        }}
        >
          Découvrez nos formules adaptées à toutes les tailles d'entreprises de transport sanitaire.
          <br />
          Un tarif transparent et sans surprise basé sur le nombre de véhicules.
        </p>

        {/* Pricing Toggle - Future enhancement for monthly/yearly pricing */}
        {/* <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '50px',
          gap: '15px',
        }}
        >
          <span style={{
            fontSize: '1.1rem',
            fontWeight: isYearly ? '400' : '600',
            color: isYearly ? '#64748b' : colors.dark,
          }}
          >Mensuel
          </span>
          <div
            onClick={() => setIsYearly(!isYearly)}
            style={{
              width: '60px',
              height: '30px',
              backgroundColor: colors.primary,
              borderRadius: '30px',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{
              position: 'absolute',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'white',
              top: '3px',
              left: isYearly ? '33px' : '3px',
              transition: 'all 0.3s ease',
            }}
            />
          </div>
          <span style={{
            fontSize: '1.1rem',
            fontWeight: isYearly ? '600' : '400',
            color: isYearly ? colors.dark : '#64748b',
          }}
          >Annuel
            {' '}
            <span style={{
              fontSize: '0.8rem',
              backgroundColor: colors.success,
              color: 'white',
              padding: '4px 8px',
              borderRadius: '20px',
              marginLeft: '5px',
            }}
            >Économisez 20%
            </span>
          </span>
        </div> */}

        <motion.div
          variants={staggerVariant}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '25px',
            marginTop: '50px',
          }}
        >
          {pricingPlans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan === plan.id}
              onSelect={() => handleSelectPlan(plan.id)}
            />
          ))}
        </motion.div>

        <motion.div
          variants={fadeInUp}
          style={{
            marginTop: '100px',
            padding: '50px',
            maxWidth: '900px',
            margin: '100px auto 0',
            backgroundColor: 'white',
            borderRadius: '20px',
            boxShadow: '0 30px 60px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(58, 134, 255, 0.1) 0%, transparent 70%)',
            top: '-150px',
            right: '-150px',
            zIndex: 0,
          }}
          />
          <div style={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 0, 110, 0.1) 0%, transparent 70%)',
            bottom: '-100px',
            left: '-100px',
            zIndex: 0,
          }}
          />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: colors.dark,
              marginBottom: '20px',
            }}
            >
              Vous avez des besoins spécifiques ?
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#64748b',
              marginBottom: '30px',
              maxWidth: '700px',
              margin: '0 auto 30px',
              lineHeight: '1.6',
            }}
            >
              Nous pouvons créer une solution personnalisée adaptée à votre entreprise de transport sanitaire.
              Contactez-nous pour discuter de vos besoins spécifiques.
            </p>
            <Link
              to="/contact#form"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                backgroundColor: colors.primary,
                color: 'white',
                padding: '16px 36px',
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: '600',
                textDecoration: 'none',
                boxShadow: '0 15px 30px rgba(58, 134, 255, 0.2)',
                transition: 'all 0.3s ease',
              }}
            >
              Contactez-nous
              <FaArrowRight />
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          style={{
            marginTop: '100px',
          }}
        >
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: colors.dark,
            marginBottom: '50px',
            position: 'relative',
            display: 'inline-block'
          }}
          >
            Questions fréquentes
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              background: 'linear-gradient(90deg, #3a86ff, #8338ec)',
              borderRadius: '2px'
            }}
            />
          </h2>

          {/* Éléments décoratifs */}
          <div style={{
            position: 'absolute',
            right: '5%',
            top: '50%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(131, 56, 236, 0.1) 0%, transparent 70%)',
            zIndex: 0
          }}
          />

          <div style={{
            position: 'absolute',
            left: '10%',
            bottom: '20%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(58, 134, 255, 0.08) 0%, transparent 70%)',
            zIndex: 0
          }}
          />

          <motion.div
            variants={staggerVariant}
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1
            }}
          >
            {faqItems.map((item, index) => (
              <motion.div key={index} variants={itemVariant}>
                <FAQItem
                  title={item.title}
                  content={item.content}
                  isOpen={openFaqIndex === index}
                  onClick={() => toggleFaqItem(index)}
                  index={index}
                />
              </motion.div>
            ))}

            {/* CTA Section */}
            <motion.div
              variants={itemVariant}
              style={{
                marginTop: '60px',
                padding: '30px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.1) 0%, rgba(131, 56, 236, 0.1) 100%)',
                border: '1px solid rgba(58, 134, 255, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                background: 'radial-gradient(circle, rgba(58, 134, 255, 0.2) 0%, transparent 70%)',
                borderRadius: '50%'
              }}
              />
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '15px',
                color: colors.dark
              }}
              >
                Une question spécifique ?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#64748b',
                marginBottom: '25px',
                maxWidth: '500px'
              }}
              >
                Notre équipe est disponible pour répondre à toutes vos questions et vous aider à choisir la formule idéale.
              </p>
              <Link
                to="/contact#form"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: 'rgba(58, 134, 255, 0.1)',
                  color: colors.primary,
                  padding: '12px 25px',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: `1px solid ${colors.primary}`,
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(58, 134, 255, 0.1)';
                  e.currentTarget.style.color = colors.primary;
                }}
              >
                Contactez-nous
                <FaArrowRight />
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default PricingPage;
